/**
 * Yama game composable following SOLID principles
 * Single Responsibility: Integrates existing yama functionality with new architecture
 * This composable acts as a bridge between the existing yama composables and our new SOLID architecture
 */
import { ref, computed, onMounted, onBeforeUnmount } from '@vue/composition-api';
import { useGameState } from '../pages/yama/composables/useGameState';
import { useConfig } from '../pages/yama/composables/useConfig';
import { useTimeTracker } from '../pages/yama/composables/useTimeTracker';
import FAQCard from '../pages/yama/modals/FAQCard.vue';
import RecordCard from '../pages/yama/modals/RecordCard.vue';
import FeatureCard from '../pages/yama/modals/featureCard.vue';

/**
 * Composable for yama page functionality
 * This integrates existing yama composables with our new architecture
 * @param {Object} props - Component props (memberLineId, groupId, etc.)
 * @param {Object} context - Vue context for accessing $cable
 * @returns {Object} Yama state and methods
 */
export function useYama(props, context) {
  // Use existing yama composables (they already follow good patterns)
  const gameState = useGameState(props, context);
  const config = useConfig(context);
  const timeTracker = useTimeTracker();
  
  // Additional state for new architecture integration
  const isInitialized = ref(false);
  
  /**
   * Initialize yama game
   * Combines initialization from existing composables
   */
  const initYamaGame = async () => {
    try {
      // The existing useGameState composable handles most initialization
      // We just need to mark as initialized
      isInitialized.value = true;
    } catch (error) {
      console.error('Failed to initialize Yama game:', error);
      throw error;
    }
  };
  
  /**
   * Handle WebSocket connection
   * Integrates with existing cable subscription
   */
  const handleWebSocketConnection = () => {
    if (context && context.root && context.root.$cable) {
      context.root.$cable.subscribe({
        channel: "AnswerRoomsChannel",
        group: `${props.groupId}`
      });
    }
  };

  /**
   * Clean up WebSocket connection
   */
  const cleanupWebSocketConnection = () => {
    if (context && context.root && context.root.$cable) {
      context.root.$cable.unsubscribe("AnswerRoomsChannel");
    }
  };
  
  /**
   * Create modal opening functions with better error handling
   */
  const createModalOpeners = () => {
    const { openModal } = config;
    
    return {
      openQuestionModal: () => {
        try {
          openModal(FAQCard)();
        } catch (error) {
          console.error('Failed to open question modal:', error);
        }
      },
      openRecordModal: () => {
        try {
          openModal(RecordCard)();
        } catch (error) {
          console.error('Failed to open record modal:', error);
        }
      },
      openFeatureModal: () => {
        try {
          openModal(FeatureCard)();
        } catch (error) {
          console.error('Failed to open feature modal:', error);
        }
      }
    };
  };
  
  // Create modal openers
  const modalOpeners = createModalOpeners();
  
  /**
   * Enhanced answer handling with validation
   * @param {Object} answerData - Answer data
   */
  const handleChooseAnswer = async (answerData) => {
    try {
      if (!answerData || !answerData.question_id || !answerData.option) {
        throw new Error('Invalid answer data');
      }
      
      await gameState.chooseAnswer(answerData);
    } catch (error) {
      console.error('Failed to choose answer:', error);
      throw error;
    }
  };
  
  /**
   * Enhanced finish answer with validation
   */
  const handleFinishAnswer = async () => {
    try {
      await gameState.finishAnswer();
    } catch (error) {
      console.error('Failed to finish answer:', error);
      throw error;
    }
  };
  
  /**
   * Enhanced action start with validation
   */
  const handleActionStart = async () => {
    try {
      await gameState.actionStart();
    } catch (error) {
      console.error('Failed to start action:', error);
      throw error;
    }
  };
  
  /**
   * Enhanced refresh game
   */
  const handleRefreshGame = async () => {
    try {
      await gameState.refreshGame();
    } catch (error) {
      console.error('Failed to refresh game:', error);
      throw error;
    }
  };
  
  /**
   * Get current game statistics
   */
  const gameStats = computed(() => {
    return {
      isInitialized: isInitialized.value,
      currentStage: gameState.currentStage && gameState.currentStage.value,
      isEnd: gameState.isEnd && gameState.isEnd.value,
      isGameOver: gameState.isGameOver && gameState.isGameOver.value,
      hasTimeRecord: gameState.hasTimeRecord && gameState.hasTimeRecord.value,
      isStartGame: gameState.isStartGame && gameState.isStartGame.value
    };
  });
  
  // Lifecycle management
  onMounted(async () => {
    await initYamaGame();
    handleWebSocketConnection();
  });
  
  onBeforeUnmount(() => {
    cleanupWebSocketConnection();
  });
  
  return {
    // State from existing composables
    ...gameState,
    ...config,
    ...timeTracker,
    
    // New architecture integration
    isInitialized,
    gameStats,
    
    // Enhanced methods
    chooseAnswer: handleChooseAnswer,
    finishAnswer: handleFinishAnswer,
    actionStart: handleActionStart,
    refreshGame: handleRefreshGame,
    
    // Modal openers
    ...modalOpeners,
    
    // Lifecycle methods
    initYamaGame,
    handleWebSocketConnection,
    cleanupWebSocketConnection,
    
    // WebSocket channel configuration (for component channels)
    webSocketChannels: {
      AnswerRoomsChannel: {
        connected() {
          console.log("Yama WebSocket connected.");
          gameState.triggerLoading();
        },
        received(data) {
          try {
            const parsedData = JSON.parse(data);
            gameState.receivedMessage(parsedData);
          } catch (error) {
            console.error('Failed to parse WebSocket message:', error);
          }
        },
        disconnected() {
          console.log("Yama WebSocket disconnected");
        },
        rejected() {
          console.log("Yama WebSocket rejected");
        }
      }
    }
  };
}